import { ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { PAYMENT_LINK_DISPATCHERS } from '@/shared/constants'
import { PRODUCT_NAMES } from '@/shared/constants/products'
import segmentService from '@/shared/services/segment.service'
import { ANALYTICS_EVENT_TYPE } from '@/packages/auth-service/requiredUserAction/shared/utils/analytics'
import { useKybGuard } from '@/shared/composables/useKybGuard'

export function usePaymentLink() {
  const store = useStore()
  const router = useRouter()
  const { ensureKyb } = useKybGuard()
  const showCreatePayment = ref(false)
  const showSuccessPayment = ref(false)
  const showPaymentLinkRecreate = ref(false)
  const element = ref(null)
  const isUpdatePaymentLink = ref(false)

  async function handlePaymentStart() {
    segmentService.trackNewEvent(ANALYTICS_EVENT_TYPE.CLICK, {
      event: ANALYTICS_EVENT_TYPE.CLICK,
      page: 'payment_links',
      screen: 'payment_links',
      button_name: 'create_payment_link',
    })

    const isPartnerLoaded = store.state.partner?.isLoaded
    const isPartnerLoading = store.state.partner?.isLoading

    if (!isPartnerLoaded && !isPartnerLoading) {
      try {
        await store.dispatch('partner/fetchPartnerInfo')
      } catch (error) {
        showCreatePayment.value = true
        return
      }
    }

    const isProductAvailable = store.getters['partner/isProductAvailable'](
      PRODUCT_NAMES.PAYMENT_LINKS
    )

    if (!isProductAvailable && store.state.partner?.isLoaded) {
      store.dispatch('ui/showProductActivationModal', 'product')
      return
    }

    showCreatePayment.value = true
  }

  async function submitCreatePayment(formData) {
    if (isUpdatePaymentLink.value) {
      const { success, payload } = await store.dispatch(
        PAYMENT_LINK_DISPATCHERS.updatePaymentLink,
        formData
      )

      if (!success) {
        return
      }

      element.value = payload
      showCreatePayment.value = false
      showSuccessPayment.value = false
      return
    }

    if (!ensureKyb()) return

    const { success, payload } = await store.dispatch(
      PAYMENT_LINK_DISPATCHERS.createPaymentLink,
      formData
    )

    if (!success) {
      return
    }

    element.value = payload
    showCreatePayment.value = false

    await router.push(`/payment-links/${payload.id}`)
  }

  async function recreatePaymentLink(formData) {
    const { success, payload } = await store.dispatch(
      PAYMENT_LINK_DISPATCHERS.recreatePaymentLink,
      formData
    )

    showPaymentLinkRecreate.value = false

    if (success && payload?.id) {
      await router.push(`/payment-links/${payload.id}`)
    }
  }

  function openUpdatePaymentLink() {
    isUpdatePaymentLink.value = true
    showCreatePayment.value = true
  }

  function resetUpdate() {
    isUpdatePaymentLink.value = false
  }

  return {
    showCreatePayment,
    showSuccessPayment,
    showPaymentLinkRecreate,
    element,
    isUpdatePaymentLink,
    handlePaymentStart,
    submitCreatePayment,
    openUpdatePaymentLink,
    resetUpdate,
    recreatePaymentLink,
  }
}
